/** Sprint and work-item queries and mutations. */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api/client'

export interface Sprint {
  id: string
  name: string
  goal: string
  /** The team that runs it. Null on mirrored sprints and on any made before teams. */
  team: string | null
  /** Teams whose work sits in this sprint — so a team's board still offers it. */
  itemTeams: string[]
  startDate: string
  endDate: string
  status: 'planned' | 'active' | 'completed'
  daysRemaining: number
  /** Mirrored from an external tracker — read-only here, lifecycle follows its dates. */
  isExternal: boolean
  externalSource: string | null
  /**
   * Items on this sprint, and how many are not Done — blocked counts as open.
   *
   * Populated by the sprint list so the completion prompt can state what it is about to
   * move before anybody chooses. Zero on a sprint returned from start or complete, which
   * do not carry counts.
   */
  itemCount: number
  openCount: number
  rowVersion: number
}

export interface WorkItem {
  id: string
  title: string
  description: string
  itemType: 'task' | 'issue' | 'change' | 'tracker'
  status: 'todo' | 'in_progress' | 'blocked' | 'done'
  priority: 'low' | 'medium' | 'high'
  points: number | null
  projectId: string | null
  sprintId: string | null
  /** One of its own project's milestones, or null. Progress only — never completes it. */
  milestoneId: string | null
  assigneeName: string
  /** Null when nobody is assigned, or when the name was typed before the picker existed. */
  assigneeUserId: string | null
  referenceUrl: string | null
  closedAt: string | null
  tracker: {
    total: number | null
    done: number | null
    /** Null when never synced — distinct from zero of zero. */
    percent: number | null
    lastSyncedAt: string | null
    lastError: string | null
    /** How old the count is, and whether the server considers it no longer trustworthy. */
    hoursSinceSync: number | null
    isStale: boolean
    /** Opens the source sheet in the browser. Null when the provider has no such location. */
    sourceWebUrl: string | null
    sourcePath: string | null
    sourceSheet: string | null
    sourceStatusColumn: string | null
    sourceDoneValue: string | null
    /** A read that succeeded but reads oddly — a zero explained by what the status column
     * actually holds. Amber, never red: the sheet was read fine. */
    lastNote: string | null
  } | null
  /** The linked change record, or null. Present on every work-item response. */
  change: {
    externalId: string
    number: string
    title: string | null
    status: string
    riskLevel: string | null
    url: string
    syncedAt: string | null
    /** The status on screen may be hours old; this is how old, and whether that matters. */
    hoursSinceSync: number | null
    isStale: boolean
    lastError: string | null
  } | null
  /** Mirrored from an external work tracker (an Azure DevOps story, issue or bug).
   * Read-only here — the sync overwrites these fields hourly, and the server refuses
   * local edits. externalUrl deep-links to the team's own board. */
  isExternal: boolean
  externalSource: string | null
  externalType: string | null
  externalUrl: string | null
  externalSyncedAt: string | null
  rowVersion: number
  modifiedDate: string
  modifiedBy: string
}

const workKeys = {
  sprints: ['sprints'] as const,
  items: (scope: Record<string, unknown>) => ['work-items', scope] as const,
  summary: (sprintId: string, assigneeUserId?: string, team?: string) =>
    ['sprints', sprintId, 'summary', assigneeUserId ?? 'everyone', team ?? 'all'] as const,
}

export function useSprints() {
  return useQuery({
    queryKey: workKeys.sprints,
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/sprints')
      return data as unknown as {
        sprints: Sprint[]
        activeSprintId: string | null
        /** Each team's own active sprint — one per team (2026-10-07). */
        activeByTeam: Record<string, string>
      }
    },
  })
}

/** The sprint's headline numbers — for one person's items when `assigneeUserId` is set. */
export function useSprintSummary(sprintId: string | null, assigneeUserId?: string, team?: string) {
  return useQuery({
    queryKey: workKeys.summary(sprintId ?? '', assigneeUserId, team),
    enabled: Boolean(sprintId),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/sprints/{sprint_id}/summary', {
        params: {
          path: { sprint_id: sprintId as string },
          query: { ...(assigneeUserId ? { assigneeUserId } : {}), ...(team ? { team } : {}) },
        },
      })
      return data as unknown as {
        itemCount: number
        committedPoints: number
        donePoints: number
        percentComplete: number
      }
    },
  })
}

/** `scope` is one of a sprint, the backlog, or the trackers — optionally narrowed to
 * one project, which is how "what is this sprint doing for project X?" is answered. */
export function useWorkItems(scope: {
  sprintId?: string
  projectId?: string
  backlog?: boolean
  trackers?: boolean
}) {
  const query: Record<string, string | boolean> = {}
  if (scope.sprintId) query.sprintId = scope.sprintId
  if (scope.projectId) query.projectId = scope.projectId
  if (scope.backlog) query.backlog = true
  if (scope.trackers) query.trackers = true

  return useQuery({
    queryKey: workKeys.items(query),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/work-items', { params: { query } })
      return data as unknown as { items: WorkItem[] }
    },
  })
}

/** Work changes move project percentages, so project views are invalidated too. */
function useInvalidateWork() {
  const client = useQueryClient()
  return () => {
    void client.invalidateQueries({ queryKey: ['work-items'] })
    void client.invalidateQueries({ queryKey: ['sprints'] })
    void client.invalidateQueries({ queryKey: ['projects'] })
    // A milestone's progress is counted from the work linked to it.
    void client.invalidateQueries({ queryKey: ['milestones'] })
  }
}

export function useCreateSprint() {
  const invalidate = useInvalidateWork()
  return useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const { data } = await api.POST('/api/v1/sprints', { body: body as never })
      return data as unknown as Sprint
    },
    onSuccess: invalidate,
  })
}

export function useUpdateSprint() {
  const invalidate = useInvalidateWork()
  return useMutation({
    mutationFn: async ({ id, ...body }: { id: string } & Record<string, unknown>) => {
      const { data } = await api.PATCH('/api/v1/sprints/{sprint_id}', {
        params: { path: { sprint_id: id } },
        body: body as never,
      })
      return data as unknown as Sprint
    },
    onSuccess: invalidate,
  })
}

export function useStartSprint() {
  const invalidate = useInvalidateWork()
  return useMutation({
    mutationFn: async (sprintId: string) => {
      const { data } = await api.POST('/api/v1/sprints/{sprint_id}/start', {
        params: { path: { sprint_id: sprintId } },
      })
      return data as unknown as Sprint
    },
    onSuccess: invalidate,
  })
}

/** Where a completed sprint's unfinished work goes. Mirrors the API's `CarryOver`. */
export type CarryOver = 'next_sprint' | 'backlog'

export interface SprintCompletionResult {
  completedCount: number
  carriedOverCount: number
  carriedTo: Sprint | null
  sentToBacklogCount: number
}

export function useCompleteSprint() {
  const invalidate = useInvalidateWork()
  return useMutation({
    mutationFn: async ({
      sprintId,
      carryOver = 'next_sprint',
      targetSprintId = null,
    }: {
      sprintId: string
      carryOver?: CarryOver
      targetSprintId?: string | null
    }) => {
      const { data } = await api.POST('/api/v1/sprints/{sprint_id}/complete', {
        params: { path: { sprint_id: sprintId } },
        body: {
          carry_over: carryOver,
          target_sprint_id: targetSprintId,
        } as never,
      })
      return data as unknown as SprintCompletionResult
    },
    onSuccess: invalidate,
  })
}

export function useCreateWorkItem() {
  const invalidate = useInvalidateWork()
  return useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const { data } = await api.POST('/api/v1/work-items', { body: body as never })
      return data as unknown as WorkItem
    },
    onSuccess: invalidate,
  })
}

/**
 * Read a tracker's spreadsheet now.
 *
 * The API answers 200 even when the read failed — a moved sheet is an operational event,
 * not an HTTP error — so the outcome is in the body and callers must check `succeeded`
 * rather than assuming a 2xx means the counts moved.
 */
export function useSyncTracker() {
  const invalidate = useInvalidateWork()
  return useMutation({
    mutationFn: async (itemId: string) => {
      const { data } = await api.POST('/api/v1/work-items/{item_id}/sync', {
        params: { path: { item_id: itemId } },
      })
      return data as unknown as {
        succeeded: boolean
        total: number | null
        done: number | null
        error: string | null
        note: string | null
        durationMs: number | null
      }
    },
    onSuccess: invalidate,
  })
}

export function useUpdateWorkItem() {
  const invalidate = useInvalidateWork()
  return useMutation({
    mutationFn: async ({ id, ...body }: { id: string } & Record<string, unknown>) => {
      const { data } = await api.PATCH('/api/v1/work-items/{item_id}', {
        params: { path: { item_id: id } },
        body: body as never,
      })
      return data as unknown as WorkItem
    },
    onSuccess: invalidate,
  })
}

export interface WorkItemComment {
  id: string
  body: string
  authorName: string
  createdAt: string
}

/** The comment thread on one work item, oldest first. Append-only — a correction is a
 * new comment — so there is no update or delete hook to pair with these. */
export function useWorkItemComments(itemId: string) {
  return useQuery({
    queryKey: ['work-items', itemId, 'comments'],
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/work-items/{item_id}/comments', {
        params: { path: { item_id: itemId } },
      })
      return data as unknown as { comments: WorkItemComment[] }
    },
  })
}

/** Posting a comment refreshes only the thread: the API deliberately leaves the item's
 * row version alone, so nothing else on the board needs refetching. */
export function useAddWorkItemComment(itemId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (body: string) => {
      const { data } = await api.POST('/api/v1/work-items/{item_id}/comments', {
        params: { path: { item_id: itemId } },
        body: { body } as never,
      })
      return data as unknown as WorkItemComment
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['work-items', itemId, 'comments'] })
    },
  })
}
